import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useEditUserResponseDialogStyles } from '../EditUserResponseDialog/useEditUserResponseDialogStyles';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { useAppDispatch } from '@application/store/useStore';
import { fetchFamilyFestAttendees, getFamilyFestAttendeeDetailAsync, completeFamilyFestRegistrationAsync } from '@application/features/familyFest/familyFestAttendeesThunk';
import { buildFamilyFestAdminRegistrationBody } from '@application/features/familyFest/build-complete-family-fest-registration-from-form';
import FamilyFormContent from '../AddFamilyResponseDialog/FamilyFormContent';
import { inferPhoneMetaFromValue } from '@ui/components/PhoneMasking/phone-format';
import { EMPTY_PERSON } from '../AddFamilyResponseDialog/FamilyPersonCard';
const toStringBoolean = (value) => value == null ? '' : value ? 'true' : 'false';
const comingWithToForm = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (normalized === 'family')
        return 'family';
    if (normalized === 'guest' || normalized === 'introduction')
        return 'guest';
    if (normalized === 'alone')
        return 'alone';
    return '';
};
const travelModeToForm = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (normalized === 'bus' || normalized === 'auto' || normalized === 'plane')
        return normalized;
    return '';
};
/** API does not persist sameAddress; infer when Google place id matches primary. */
const sameAddressAsPrimaryOnLoad = (primary, member) => {
    const pPlace = String(primary.addressPlaceId ?? '').trim();
    const mPlace = String(member.addressPlaceId ?? '').trim();
    return Boolean(pPlace && mPlace && pPlace === mPlace);
};
const EditFamilyResponseDialog = ({ open, onClose, userId, userName: _userName, eventId, paginationModel: _paginationModel, onRefreshAttendees, isWaitingList = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1200));
    const styles = useEditUserResponseDialogStyles();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [familyIsSaved, setFamilyIsSaved] = useState(false);
    const [invitationId, setInvitationId] = useState('');
    const [isWaitlistEntry, setIsWaitlistEntry] = useState(false);
    const [maxPersons, setMaxPersons] = useState(null);
    const [originalMemberIds, setOriginalMemberIds] = useState([]);
    // ComingWith as saved, and the saved members put aside while another type is selected (see effect below).
    const originalComingWithRef = useRef(null);
    const stashedMembersRef = useRef([]);
    const { control, setValue, getValues, reset, trigger, formState: { errors } } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            persons: [EMPTY_PERSON]
        }
    });
    // Same behaviour as the public form: when ComingWith changes away from the saved type (Family <-> Guest, or to
    // Alone), the saved members are not carried over - they are removed from the form, so the save deletes them -
    // and members added under another type are dropped. Switching back to the saved type restores them.
    const watchedComingWith = useWatch({ control, name: 'persons.0.ComingWith' });
    useEffect(() => {
        const original = originalComingWithRef.current;
        const current = String(watchedComingWith ?? '').toLowerCase().trim();
        if (original === null || !current)
            return;
        const [primary, ...members] = getValues('persons') ?? [];
        if (!primary)
            return;
        if (current === original) {
            if (stashedMembersRef.current.length > 0) {
                setValue('persons', [primary, ...stashedMembersRef.current], { shouldDirty: true });
                stashedMembersRef.current = [];
            }
            return;
        }
        if (stashedMembersRef.current.length === 0) {
            stashedMembersRef.current = members.filter((member) => String(member?.id ?? '').trim());
        }
        if (members.length > 0) {
            setValue('persons', [primary], { shouldDirty: true });
        }
    }, [watchedComingWith, getValues, setValue]);
    // Populate family form using Family Fest attendee details
    useEffect(() => {
        if (!open || !userId)
            return;
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            try {
                const data = await dispatch(getFamilyFestAttendeeDetailAsync(userId)).unwrap();
                if (!data.isSuccess || !data.data) {
                    throw new Error(data.message || 'Failed to load attendee details');
                }
                if (cancelled)
                    return;
                const detail = data.data;
                setInvitationId(detail.invitationId ?? '');
                setIsWaitlistEntry(isWaitingList && ['Waitlisted', '8', 'Invited', '4'].includes(String(detail.invitationStatus ?? '')));
                setMaxPersons(detail.expectedNoOfPersons ?? null);
                setOriginalMemberIds((detail.members ?? []).map((member) => member.id).filter(Boolean));
                const primaryAddressCountry = detail.addressCountry ?? 'nl';
                const primaryPhoneMeta = inferPhoneMetaFromValue(detail.phone, primaryAddressCountry);
                const primaryTravelMode = travelModeToForm(detail.travelMode);
                const primary = {
                    ...EMPTY_PERSON,
                    id: detail.id,
                    firstName: detail.firstName ?? '',
                    middleName: detail.middleName ?? '',
                    lastName: detail.lastName ?? '',
                    company: detail.companyId ?? '',
                    email: detail.email ?? '',
                    eventChoice: toStringBoolean(detail.isComingToEvent),
                    attendanceStatus: detail.attendanceStatus ? String(detail.attendanceStatus) : undefined,
                    reasonForCancellation: detail.reasonForCancellation ?? '',
                    postalcode: detail.postalCode ?? '',
                    houseNumber: detail.houseNumber ?? '',
                    addition: detail.houseNumberAddition ?? '',
                    Address: detail.addressLine ?? '',
                    addressCountry: primaryAddressCountry,
                    addressPlaceId: detail.addressPlaceId ?? '',
                    addressLatitude: detail.addressLatitude ?? null,
                    addressLongitude: detail.addressLongitude ?? null,
                    Phone: primaryPhoneMeta.phoneDigits,
                    PhoneCountryCode: primaryPhoneMeta.countryCode,
                    PhoneDialCode: primaryPhoneMeta.dialCode,
                    DateOfBirth: detail.dateOfBirth ?? null,
                    TravelMode: primaryTravelMode,
                    transportLocation: primaryTravelMode === 'plane'
                        ? (detail.preferredAirport ?? '')
                        : (detail.pickupLocationId ?? ''),
                    pickupLocationName: detail.pickupLocationName ?? '',
                    isPickupAccommodationAvailable: detail.isPickupAccommodationAvailable ?? false,
                    accommodationRequired: toStringBoolean(detail.wantsVdkAccommodation),
                    Option: toStringBoolean(detail.isWheelchairDependent),
                    supportComments: detail.mobilityComments ?? '',
                    bookingInfoRequired: toStringBoolean(detail.hasHotelBooking),
                    bookingInfo: detail.hotelBookingComments ?? '',
                    ComingWith: comingWithToForm(detail.comingWith),
                    TermsAccepted: ['true']
                };
                const members = (detail.members ?? []).map((member) => {
                    const memberAddressCountry = member.addressCountry ?? primary.addressCountry ?? 'nl';
                    const memberPhoneMeta = inferPhoneMetaFromValue(member.phone, memberAddressCountry);
                    const memberTravelMode = travelModeToForm(member.travelMode);
                    return {
                        ...EMPTY_PERSON,
                        id: member.id,
                        firstName: member.firstName ?? '',
                        middleName: member.middleName ?? '',
                        lastName: member.lastName ?? '',
                        relationshipToYou: member.relationshipToPrimary ?? '',
                        email: member.email ?? '',
                        eventChoice: toStringBoolean(member.isComingToEvent),
                        attendanceStatus: member.attendanceStatus ? String(member.attendanceStatus) : undefined,
                        reasonForCancellation: member.reasonForCancellation ?? '',
                        postalcode: member.postalCode ?? '',
                        houseNumber: member.houseNumber ?? '',
                        addition: member.houseNumberAddition ?? '',
                        Address: member.addressLine ?? '',
                        addressCountry: memberAddressCountry,
                        addressPlaceId: member.addressPlaceId ?? '',
                        addressLatitude: member.addressLatitude ?? null,
                        addressLongitude: member.addressLongitude ?? null,
                        Phone: memberPhoneMeta.phoneDigits,
                        PhoneCountryCode: memberPhoneMeta.countryCode,
                        PhoneDialCode: memberPhoneMeta.dialCode,
                        DateOfBirth: member.dateOfBirth ?? null,
                        TravelMode: memberTravelMode,
                        transportLocation: memberTravelMode === 'plane'
                            ? (member.preferredAirport ?? '')
                            : (member.pickupLocationId ?? ''),
                        pickupLocationName: member.pickupLocationName ?? '',
                        isPickupAccommodationAvailable: member.isPickupAccommodationAvailable ?? false,
                        accommodationRequired: toStringBoolean(member.wantsVdkAccommodation),
                        Option: toStringBoolean(member.isWheelchairDependent),
                        supportComments: member.mobilityComments ?? '',
                        bookingInfoRequired: toStringBoolean(member.hasHotelBooking),
                        bookingInfo: member.hotelBookingComments ?? '',
                        sameAddress: sameAddressAsPrimaryOnLoad(primary, member),
                        TermsAccepted: ['true']
                    };
                });
                const persons = [primary, ...members];
                const parsedVehicleCount = Number(detail.vehicleCount);
                reset({
                    persons,
                    vehicleCount: detail.vehicleCount &&
                        detail.vehicleCount !== '' &&
                        Number.isFinite(parsedVehicleCount) &&
                        parsedVehicleCount > 0
                        ? parsedVehicleCount
                        : null
                });
                setFamilyIsSaved(String(primary.ComingWith ?? '').toLowerCase().trim() === 'alone');
                originalComingWithRef.current = String(primary.ComingWith ?? '').toLowerCase().trim();
                stashedMembersRef.current = [];
            }
            catch (error) {
                if (!cancelled) {
                    toast.error(error instanceof Error ? error.message : 'Failed to load attendee details');
                }
            }
            finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [open, reset, userId]);
    // Clear form data when dialog closes
    useEffect(() => {
        if (!open) {
            reset({ persons: [EMPTY_PERSON], vehicleCount: null });
            setFamilyIsSaved(false);
            setIsLoading(false);
            setInvitationId('');
            setMaxPersons(null);
            originalComingWithRef.current = null;
            stashedMembersRef.current = [];
            setOriginalMemberIds([]);
        }
    }, [open, reset]);
    const refreshGrid = () => {
        if (onRefreshAttendees) {
            onRefreshAttendees();
            return;
        }
        if (eventId) {
            dispatch(fetchFamilyFestAttendees({
                eventId,
                req: {
                    isArchived: false,
                    isComingToEvent: true,
                    sortBy: 'createdon',
                    sortOrder: 'desc'
                }
            }));
        }
    };
    const handleFamilySave = async () => {
        if (!userId)
            return;
        const values = getValues();
        const persons = values.persons || [];
        const primary = persons[0];
        if (!primary)
            return;
        const comingWith = String(primary?.ComingWith ?? '')
            .toLowerCase()
            .trim();
        const shortFlow = comingWith === 'alone' || primary?.eventChoice === 'false';
        const isValid = shortFlow ? await trigger('persons.0') : await trigger();
        if (!isValid)
            return;
        // Waiting-list entries: coming with someone needs at least one other person, or the entry is saved half done.
        if (isWaitlistEntry && !shortFlow && persons.length < 2) {
            toast.error(t('events.eventDetails.responsesTab.familyNeedsMember', 'Add at least one person, or set coming alone.'));
            return;
        }
        if (!invitationId) {
            toast.error('Invitation id is missing for this attendee.');
            return;
        }
        setIsSaving(true);
        try {
            const currentMemberIds = (persons.slice(1) ?? [])
                .map((person) => String(person?.id ?? '').trim())
                .filter(Boolean);
            const deletedMemberIds = originalMemberIds.filter((id) => !currentMemberIds.includes(id));
            const registrationBody = buildFamilyFestAdminRegistrationBody(values, deletedMemberIds);
            const requestBody = {
                invitationId,
                ...registrationBody
            };
            const data = await dispatch(completeFamilyFestRegistrationAsync(requestBody)).unwrap();
            if (!data.isSuccess) {
                throw new Error(data.message || 'Failed to save attendee');
            }
            toast.success(t('events.eventDetails.responsesTab.responseUpdatedSuccessfully') ||
                'Response updated successfully!');
            refreshGrid();
            onClose();
        }
        catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to update response. Please try again.');
        }
        finally {
            setIsSaving(false);
        }
    };
    const familyFormContent = (_jsx(FamilyFormContent, { control: control, errors: errors, setValue: setValue, isSaved: familyIsSaved, activeStep: 1, onStepChange: () => { }, isEditMode: true, maxPersons: maxPersons, savedPersonCount: originalMemberIds.length + 1, savedComingWith: originalComingWithRef.current }));
    const actionButtons = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, disabled: isSaving, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', endIcon: _jsx(CheckOutlined, {}), onClick: handleFamilySave, disabled: isSaving || isLoading, children: isSaving || isLoading
                    ? t('actions.saving') || 'Saving...'
                    : t('actions.saveChanges') || 'Save Changes' })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { anchor: 'bottom', title: t('events.eventDetails.responsesTab.editUserResponse'), open: open, onClose: onClose, showCloseButton: true, dialogActions: actionButtons, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: _jsx(Box, { sx: { px: 2, py: 2 }, children: familyFormContent }) }) }));
    }
    return (_jsx(CustomDialog, { backgroundVariant: 'white', titleWithIcon: t('events.eventDetails.responsesTab.editUserResponse'), open: open, onClose: onClose, maxWidth: 'md', titleIcon: '/assets/images/dialog-pencil.png', actions: actionButtons, children: _jsx(Box, { sx: styles.dialogContentStyle, children: _jsx(Box, { sx: { mt: 2, px: 2 }, children: familyFormContent }) }) }));
};
export default EditFamilyResponseDialog;
